import React, { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { gsap, ScrollTrigger, MOTION_OK } from '../animations/gsap';

/**
 * Route-change transition: a quick fade of the page content plus a brand-gradient progress sweep
 * at the top of the window. Content is rendered immediately (opacity only, no delay, no transform
 * so pinned sections keep working), and nothing runs on the first load.
 * Page-level GSAP animations are cleaned up by each page's useGsapReveal hook on unmount.
 */
export default function PageTransition({ children }) {
  const { pathname } = useLocation();
  const contentRef = useRef(null);
  const barRef = useRef(null);
  const firstRender = useRef(true);

  useLayoutEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return undefined;
    }
    const mm = gsap.matchMedia();
    mm.add(MOTION_OK, () => {
      gsap.fromTo(
        contentRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.45, ease: 'power1.out', clearProps: 'opacity' }
      );
      gsap
        .timeline()
        .fromTo(barRef.current, { scaleX: 0, opacity: 1 }, { scaleX: 1, duration: 0.55, ease: 'power2.out' })
        .to(barRef.current, { opacity: 0, duration: 0.25, ease: 'power1.out' });
    });
    // New page content (and lazily loaded route chunks) changes the layout: re-measure triggers.
    const id = window.setTimeout(() => ScrollTrigger.refresh(), 350);
    return () => {
      window.clearTimeout(id);
      mm.revert();
    };
  }, [pathname]);

  return (
    <>
      <div
        ref={barRef}
        aria-hidden="true"
        className="pointer-events-none fixed left-0 right-0 top-0 z-[10000] h-[3px] origin-left bg-gradient-to-r from-orange-500 to-yellow-400 opacity-0"
      />
      <div ref={contentRef}>{children}</div>
    </>
  );
}
